import { useState, useEffect } from 'react';
import { Bell, BellOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { 
  isPushSupported, 
  getNotificationPermission, 
  requestNotificationPermission, 
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed
} from '@/services/pushNotifications';
import { toast } from 'sonner';

export function NotificationSettings({ userId, userType }) {
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    if (!isPushSupported()) {
      setSupported(false);
      return;
    }
    
    setPermission(getNotificationPermission());
    const isSub = await isSubscribed();
    setSubscribed(isSub);
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    try {
      const result = await requestNotificationPermission();
      setPermission(result);
      
      if (result === 'granted') {
        const sub = await subscribeToPush(userId, userType);
        if (sub) {
          setSubscribed(true);
          toast.success('Notifications enabled successfully!');
        }
      } else if (result === 'denied') {
        toast.error('Notifications blocked. Please enable in browser settings.');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast.error('Failed to enable notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setLoading(true);
    try {
      await unsubscribeFromPush(userId);
      setSubscribed(false);
      toast.success('Notifications disabled');
    } catch (error) {
      console.error('Error disabling notifications:', error);
      toast.error('Failed to disable notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (enabled) => {
    if (enabled) {
      await handleEnableNotifications();
    } else {
      await handleDisableNotifications();
    }
  };

  if (!supported) {
    return (
      <div className="p-4 bg-card rounded-xl border border-border/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center">
            <BellOff className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium">Push Notifications</h3>
            <p className="text-xs text-muted-foreground">Not supported in this browser</p>
          </div>
        </div>
      </div>
    );
  }

  // If permission was denied by browser
  if (permission === 'denied') {
    return (
      <div className="p-4 bg-card rounded-xl border border-border/50">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-destructive" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium">Notifications Blocked</h3>
            <p className="text-xs text-muted-foreground mt-1">
              You have blocked notifications. To enable them:
            </p>
            <ol className="text-xs text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
              <li>Click the lock icon in your browser's address bar</li>
              <li>Find "Notifications" and change to "Allow"</li>
              <li>Refresh this page</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-card rounded-xl border border-border/50" data-testid="notification-settings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            subscribed ? 'bg-primary/10' : 'bg-muted/50'
          }`}>
            {subscribed ? (
              <Bell className="w-5 h-5 text-primary" />
            ) : (
              <BellOff className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
          <div>
            <h3 className="text-sm font-medium">Push Notifications</h3>
            <p className="text-xs text-muted-foreground">
              {subscribed 
                ? 'Get instant updates about your rides' 
                : 'Enable to receive ride updates'}
            </p>
          </div>
        </div>
        
        <Switch
          checked={subscribed}
          onCheckedChange={handleToggle}
          disabled={loading}
          data-testid="notification-toggle"
        />
      </div>

      {permission === 'default' && !subscribed && (
        <div className="mt-3 pt-3 border-t border-border/50">
          <Button 
            size="sm" 
            onClick={handleEnableNotifications}
            disabled={loading}
            className="w-full"
            data-testid="enable-notifications-btn"
          >
            {loading ? 'Enabling...' : 'Enable Notifications'}
          </Button>
        </div>
      )}

      {subscribed && (
        <div className="mt-3 pt-3 border-t border-border/50">
          <div className="flex items-center gap-2 text-xs text-green-600">
            <CheckCircle2 className="w-4 h-4" />
            <span>You'll receive notifications for ride updates</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationSettings;
